// <purpose>The ONE by-name join of a ticket's attachments and its OCR decisions, plus the German
// labels and byte formatting the Anhänge section renders. ReanalysisModal's tri-state seeding uses
// the same index so "which decision belongs to which file" is decided in exactly one place.</purpose>
// <dependencies>types.ts (AttachmentInfo, OcrDecision) — DTO fields the detail endpoint already serves.</dependencies>
// <constraints>Join key is the attachment NAME (what ocrDecisions carries) — two attachments with the
// same name collapse onto one decision, same as ReanalysisModal always did. 'unknown' = no decision
// row for the name (pre-decision tickets, nested forms) — never rendered as "not read".
// INLINE PRECEDENCE (isInlineAttachment, the ONE place this is decided): the ingest stamp
// (att.isInline) is a parse-time measurement and wins outright, true or false; the OCR decision
// (reason === 'inline') is a processing-time inference that only answers for rows written before
// the stamp existed (isInline absent). Neither present ⇒ document — fails OPEN, so an
// undescribable file stays visible and counted instead of being silently demoted to decoration.
import type { AttachmentInfo, OcrDecision } from '../serverTypes';
import { formatDeOneDecimal } from '../utils/formatting';

export type AttachmentOcrState = 'read' | 'not_read' | 'unknown';

export interface AttachmentOcrRow {
  name: string;
  size?: number;
  state: AttachmentOcrState;
  decidedBy?: OcrDecision['decidedBy'];
  reason?: string | null;
  // Resolved by isInlineAttachment at join time — never optional, so no consumer re-derives it.
  isInline: boolean;
}

export const OCR_STATE_LABELS: Record<AttachmentOcrState, string> = {
  read: 'gelesen',
  not_read: 'nicht gelesen',
  unknown: '—',
};

export const OCR_DECIDER_LABELS: Record<OcrDecision['decidedBy'], string> = {
  ai: 'KI',
  rep: 'Sachbearbeiter',
  system: 'System',
};

// German label for the machine-readable `reason` token core sends (see TicketPipelineService.cs).
// Fail-open: an unmapped token passes through untranslated rather than vanishing — a new core
// reason must stay visible even before this map is updated for it.
export const OCR_REASON_LABELS: Record<string, string> = {
  inline: 'Eingebettetes Bild',
  // Not-OCR-able formats (e.g. .docx) — no count-bearing surface, so singular only; the plural
  // fallback in ocrReasonLabelCount below covers it.
  unsupported_format: 'Format nicht lesbar',
};

export const ocrReasonLabel = (reason: string | null | undefined): string | null =>
  reason ? (OCR_REASON_LABELS[reason] ?? reason) : null;

// German plurals are not derivable by suffixing (eingebettetes Bild → eingebettete Bilder) —
// stated as a sibling fact beside the singular, the same pattern VocabularyAxis.nounPlural uses
// for the tenant's own axis nouns. One caller today is fine: this map is the HOME for the fact,
// so the moment a second reason needs a count line it lands in the right place instead of inline.
export const OCR_REASON_LABELS_PLURAL: Record<string, string> = {
  inline: 'eingebettete Bilder',
};

// Count-aware sibling of ocrReasonLabel, and fail-open in the same two ways: an unmapped reason
// passes through as its raw token (a new core reason stays VISIBLE rather than vanishing), and a
// reason with no plural entry falls back to its singular — wrong-but-obvious beats absent.
export const ocrReasonLabelCount = (reason: string, n: number): string =>
  n === 1
    ? OCR_REASON_LABELS[reason] ?? reason
    : OCR_REASON_LABELS_PLURAL[reason] ?? OCR_REASON_LABELS[reason] ?? reason;

const lowerFirst = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
const upperFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// The GROUP SUBHEADER over a reason's own rows (the "Eingebettete Bilder" block the two OCR control
// surfaces render below the documents when the tenant counts inline images separately). Same plural
// fact inlineSummaryLabel renders, capitalised because a heading opens a line instead of following
// a numeral — which is precisely why it needs a home here rather than a German string literal at
// each surface: the fact lives once, the two grammatical forms are both derived from it.
export const ocrReasonGroupHeading = (reason: string): string =>
  upperFirst(ocrReasonLabelCount(reason, 2));

// The muted line under the Anhänge rows. Every word comes from the label maps, so this line and
// the chip rendered beside it cannot drift. Lower-cased because German writes the adjective small
// after a numeral ("2 eingebettete Bilder"), while the map holds the display-capitalised form.
export const inlineSummaryLabel = (count: number): string =>
  `${count} ${lowerFirst(ocrReasonLabelCount('inline', count))} — ${OCR_STATE_LABELS.not_read}`;

export const indexOcrDecisionsByName = (decisions: OcrDecision[] | undefined): Map<string, OcrDecision> =>
  new Map((decisions ?? []).map(d => [d.name, d]));

// The inline verdict, decided in exactly one place. PRECEDENCE, and it is load-bearing: the ingest
// STAMP is a parse-time measurement of the mail itself and wins outright; the OCR decision is a
// processing-time inference and only answers for rows written before the stamp existed. Neither
// present ⇒ document, which fails OPEN — a file the system cannot describe stays visible and
// counted rather than being silently demoted to decoration.
export const isInlineAttachment = (att: AttachmentInfo, decision?: OcrDecision): boolean => {
  if (typeof att.isInline === 'boolean') return att.isInline;
  return decision?.reason === 'inline';
};

export const resolveAttachmentOcrRows = (
  attachments: AttachmentInfo[] | undefined,
  decisions: OcrDecision[] | undefined,
): AttachmentOcrRow[] => {
  if (!attachments?.length) return [];
  const byName = indexOcrDecisionsByName(decisions);
  return attachments.map(att => {
    const d = byName.get(att.name);
    const isInline = isInlineAttachment(att, d);
    if (!d) return { name: att.name, size: att.size, state: 'unknown', isInline };
    return { name: att.name, size: att.size, state: d.ocr ? 'read' : 'not_read', decidedBy: d.decidedBy, reason: d.reason ?? null, isInline };
  });
};

export const partitionAttachmentRows = (rows: AttachmentOcrRow[]): {
  documents: AttachmentOcrRow[];
  inline: AttachmentOcrRow[];
} => ({
  documents: rows.filter(r => !r.isInline),
  inline: rows.filter(r => r.isInline),
});

// THE count every surface renders. `countInline` is the tenant's UI_ATTACHMENTS_COUNT_INLINE
// posture — true (the absent default) reproduces the behaviour that shipped before this rule.
export const countAttachments = (rows: AttachmentOcrRow[], countInline: boolean): number =>
  countInline ? rows.length : partitionAttachmentRows(rows).documents.length;

// One decimal, German comma; sub-KB sizes are not worth a decimal.
export const formatBytes = (bytes: number | undefined): string => {
  if (bytes == null) return '—';
  if (bytes < 1024) return '< 1 KB';
  const kb = bytes / 1024;
  // Round before branching: an unrounded kb near the unit boundary (e.g. 1023.95) would otherwise
  // display as the misleading "1.024,0 KB" instead of crossing into MB — round first, then decide.
  if (Math.round(kb) >= 1024) {
    return `${formatDeOneDecimal(bytes / 1024 / 1024)} MB`;
  }
  return `${formatDeOneDecimal(kb)} KB`;
};

// A file name cut in the MIDDLE (compare mode's chips, 2026-10-30): the head and the last 8
// characters stay, so a name's ending (its type, its date) survives and two files that share a
// long head stay apart. Names within the budget are returned whole.
export const shortenFileName = (name: string, max = 32, tail = 8): string => {
  if (name.length <= max) return name;
  const head = Math.max(1, max - tail - 1);
  return `${name.slice(0, head)}…${name.slice(-tail)}`;
};

// The count beside „Vergleichen", said in full on hover: „2 Anhänge", and the inline images the
// tenant does not count when there are any — „1 Anhang, 3 eingebettete Bilder".
export const attachmentCountTitle = (counted: number, uncountedInline: number): string => {
  const files = `${counted} ${counted === 1 ? 'Anhang' : 'Anhänge'}`;
  return uncountedInline > 0 ? `${files}, ${uncountedInline} ${lowerFirst(ocrReasonLabelCount('inline', uncountedInline))}` : files;
};
